import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../config/api';

const estados = [
  { id: 'pendiente', label: 'Pendientes' },
  { id: 'aprobada', label: 'Aprobadas' },
  { id: 'utilizada', label: 'Utilizadas' },
  { id: 'rechazada', label: 'Rechazadas' },
  { id: '', label: 'Todas' },
];

export default function GestionSolicitudesRepuestos() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [depositos, setDepositos] = useState([]);
  const [stockDepositos, setStockDepositos] = useState([]);
  const [usuariosDepositos, setUsuariosDepositos] = useState([]);
  const [estadoFiltro, setEstadoFiltro] = useState('pendiente');
  const [cargando, setCargando] = useState(false);
  const [procesandoId, setProcesandoId] = useState(null);
  const [solicitudAprobar, setSolicitudAprobar] = useState(null);
  const [aprobacionForm, setAprobacionForm] = useState({
    modo_descuento: 'automatico',
    deposito_id: '',
    observacion_admin: '',
  });

  const headersActor = useMemo(
    () => ({
      'Content-Type': 'application/json',
      'X-Actor-Rol': localStorage.getItem('rolAdmin') || '',
      'X-Actor-Id': localStorage.getItem('adminId') || '',
    }),
    []
  );

  const cargarSolicitudes = async () => {
    setCargando(true);
    try {
      const params = new URLSearchParams();
      if (estadoFiltro) params.append('estado', estadoFiltro);

      const res = await fetch(`${API_BASE}/api/solicitudes-repuestos?${params.toString()}`);
      const data = await res.json().catch(() => []);

      setSolicitudes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error cargando solicitudes de repuestos:', error);
      setSolicitudes([]);
    } finally {
      setCargando(false);
    }
  };

  const cargarContextoStock = async () => {
    try {
      const [resDepositos, resStock, resUsuariosDepositos] = await Promise.all([
        fetch(`${API_BASE}/api/depositos`),
        fetch(`${API_BASE}/api/inventario/stock-depositos`),
        fetch(`${API_BASE}/api/tecnicos-depositos`),
      ]);

      const dataDepositos = resDepositos.ok ? await resDepositos.json() : [];
      const dataStock = resStock.ok ? await resStock.json() : [];
      const dataUsuariosDepositos = resUsuariosDepositos.ok ? await resUsuariosDepositos.json() : [];

      setDepositos(Array.isArray(dataDepositos) ? dataDepositos : []);
      setStockDepositos(Array.isArray(dataStock) ? dataStock : []);
      setUsuariosDepositos(Array.isArray(dataUsuariosDepositos) ? dataUsuariosDepositos : []);
    } catch (error) {
      console.error('Error cargando stock por depósitos:', error);
      setDepositos([]);
      setStockDepositos([]);
      setUsuariosDepositos([]);
    }
  };

  useEffect(() => {
    cargarSolicitudes();
  }, [estadoFiltro]);

  useEffect(() => {
    cargarContextoStock();
  }, []);

  const adminId = Number(localStorage.getItem('adminId') || 0);

  const depositosAdminIds = useMemo(() => {
    const usuario = usuariosDepositos.find((item) => Number(item.usuario_id || item.tecnico_id) === adminId);
    return String(usuario?.deposito_ids || '')
      .split(',')
      .map((id) => Number(id))
      .filter(Boolean);
  }, [usuariosDepositos, adminId]);

  const depositosAdmin = useMemo(
    () => depositos.filter((deposito) => depositosAdminIds.includes(Number(deposito.id))),
    [depositos, depositosAdminIds]
  );

  const stockEnDeposito = (componenteId, depositoId) => {
    const row = stockDepositos.find(
      (item) =>
        Number(item.componente_id) === Number(componenteId) &&
        Number(item.deposito_id) === Number(depositoId)
    );
    return row ? Number(row.cantidad || 0) : 0;
  };

  const totalStockEnDepositos = (detalle, depositoIds) =>
    depositoIds.reduce((total, depositoId) => total + stockEnDeposito(detalle.componente_id, depositoId), 0);

  const abrirModalAprobacion = (solicitud) => {
    setSolicitudAprobar(solicitud);
    setAprobacionForm({
      modo_descuento: depositosAdminIds.length ? 'automatico' : 'manual',
      deposito_id: '',
      observacion_admin: '',
    });
  };

  const cerrarModalAprobacion = () => {
    setSolicitudAprobar(null);
    setAprobacionForm({ modo_descuento: 'automatico', deposito_id: '', observacion_admin: '' });
    setProcesandoId(null);
  };

  const aprobarSolicitud = async () => {
    const solicitud = solicitudAprobar;
    if (!solicitud?.id) return;

    if (aprobacionForm.modo_descuento === 'manual' && !aprobacionForm.deposito_id) {
      alert('Seleccioná el depósito de origen para aprobar.');
      return;
    }

    setProcesandoId(solicitud.id);

    try {
      const res = await fetch(`${API_BASE}/api/solicitudes-repuestos/${solicitud.id}/aprobar`, {
        method: 'PUT',
        headers: headersActor,
        body: JSON.stringify({
          observacion_admin: aprobacionForm.observacion_admin,
          modo_descuento: aprobacionForm.modo_descuento,
          deposito_id: aprobacionForm.modo_descuento === 'manual' ? aprobacionForm.deposito_id : null,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data?.mensaje || 'No se pudo aprobar la solicitud');
        return;
      }

      alert(data?.mensaje || 'Solicitud aprobada');
      await cargarSolicitudes();
      await cargarContextoStock();
      cerrarModalAprobacion();
    } catch (error) {
      console.error('Error aprobando solicitud:', error);
      alert('Error de conexión al aprobar');
    } finally {
      setProcesandoId(null);
    }
  };

  const rechazarSolicitud = async (solicitud) => {
    const observacion = window.prompt('Motivo del rechazo:');
    if (observacion === null) return;

    setProcesandoId(solicitud.id);

    try {
      const res = await fetch(`${API_BASE}/api/solicitudes-repuestos/${solicitud.id}/rechazar`, {
        method: 'PUT',
        headers: headersActor,
        body: JSON.stringify({ observacion_admin: observacion }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data?.mensaje || 'No se pudo rechazar la solicitud');
        return;
      }

      alert(data?.mensaje || 'Solicitud rechazada');
      cargarSolicitudes();
    } catch (error) {
      console.error('Error rechazando solicitud:', error);
      alert('Error de conexión al rechazar');
    } finally {
      setProcesandoId(null);
    }
  };

  const badgeEstado = (estado) => {
    if (estado === 'aprobada') return 'bg-blue-100 text-blue-800';
    if (estado === 'utilizada') return 'bg-green-100 text-green-800';
    if (estado === 'rechazada') return 'bg-red-100 text-red-800';
    if (estado === 'cancelada') return 'bg-gray-100 text-gray-700';
    return 'bg-yellow-100 text-yellow-800';
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold text-blue-700">Solicitudes de repuestos</h2>
          <p className="text-sm text-gray-600 mt-1">
            Aprobá o rechazá repuestos solicitados para servicios técnicos.
          </p>
        </div>

        <button
          type="button"
          onClick={cargarSolicitudes}
          className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white"
        >
          Actualizar
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {estados.map((estado) => (
          <button
            key={estado.id || 'todas'}
            type="button"
            onClick={() => setEstadoFiltro(estado.id)}
            className={`px-3 py-2 border rounded text-sm ${
              estadoFiltro === estado.id
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            {estado.label}
          </button>
        ))}
      </div>

      {cargando ? (
        <div className="p-4 bg-white rounded border text-gray-600">Cargando solicitudes...</div>
      ) : solicitudes.length === 0 ? (
        <div className="p-4 bg-white rounded border text-gray-500">No hay solicitudes para mostrar.</div>
      ) : (
        <div className="space-y-4">
          {solicitudes.map((solicitud) => (
            <div key={solicitud.id} className="bg-white rounded-lg border shadow p-4">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-bold text-lg text-gray-900">
                      Solicitud #{solicitud.id}
                    </h3>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${badgeEstado(solicitud.estado)}`}>
                      {solicitud.estado === 'aprobada' ? 'transferida' : solicitud.estado}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mt-1">
                    Pedido #{solicitud.pedido_id} · {solicitud.pedido?.colegio || 'Sin colegio'} ·{' '}
                    {solicitud.pedido?.sala || 'Sin sala'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Técnico: {solicitud.tecnico_usuario || 'No identificado'} ·{' '}
                    {solicitud.fecha_solicitud
                      ? new Date(solicitud.fecha_solicitud).toLocaleString()
                      : '-'}
                  </p>
                </div>

                {solicitud.estado === 'pendiente' && (
                  <div className="flex flex-col md:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => abrirModalAprobacion(solicitud)}
                      disabled={procesandoId === solicitud.id}
                      className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
                    >
                      Aprobar
                    </button>
                    <button
                      type="button"
                      onClick={() => rechazarSolicitud(solicitud)}
                      disabled={procesandoId === solicitud.id}
                      className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
                    >
                      Rechazar
                    </button>
                  </div>
                )}
              </div>

              {solicitud.observacion_tecnico && (
                <div className="mt-3 p-3 rounded bg-gray-50 text-sm text-gray-700">
                  <span className="font-semibold">Observación técnica:</span>{' '}
                  {solicitud.observacion_tecnico}
                </div>
              )}

              {solicitud.observacion_admin && (
                <div className="mt-3 p-3 rounded bg-blue-50 text-sm text-gray-700">
                  <span className="font-semibold">Respuesta admin:</span>{' '}
                  {solicitud.observacion_admin}
                </div>
              )}

              <div className="mt-4 hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white rounded">
                  <thead>
                    <tr className="bg-blue-100 text-left">
                      <th className="px-3 py-2">Repuesto</th>
                      <th className="px-3 py-2">Solicitado</th>
                      <th className="px-3 py-2">Aprobado</th>
                      <th className="px-3 py-2">Usado</th>
                      <th className="px-3 py-2">Stock actual</th>
                      <th className="px-3 py-2">Mis depósitos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(solicitud.detalles || []).map((detalle) => (
                      <tr key={detalle.id} className="border-t">
                        <td className="px-3 py-2">
                          <div className="font-semibold">{detalle.nombre}</div>
                          <div className="text-xs text-gray-500">{detalle.codigo_unico}</div>
                        </td>
                        <td className="px-3 py-2">{detalle.cantidad_solicitada}</td>
                        <td className="px-3 py-2">{detalle.cantidad_aprobada || '-'}</td>
                        <td className="px-3 py-2">{detalle.cantidad_utilizada || '-'}</td>
                        <td className="px-3 py-2">{detalle.stock_actual}</td>
                        <td className="px-3 py-2">
                          {depositosAdmin.length ? (
                            <div className="text-sm text-gray-700">
                              {depositosAdmin.map((deposito) => (
                                <div key={deposito.id}>
                                  {deposito.nombre}: {stockEnDeposito(detalle.componente_id, deposito.id)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">Sin depósitos asignados</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 md:hidden space-y-3">
                {(solicitud.detalles || []).map((detalle) => (
                  <div key={detalle.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="font-semibold text-gray-900">{detalle.nombre}</div>
                    <div className="text-xs text-gray-500">{detalle.codigo_unico}</div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                      <div className="rounded bg-gray-50 p-2">
                        <div className="text-gray-500">Solicitado</div>
                        <div className="font-semibold">{detalle.cantidad_solicitada}</div>
                      </div>
                      <div className="rounded bg-blue-50 p-2">
                        <div className="text-gray-500">Aprobado</div>
                        <div className="font-semibold">{detalle.cantidad_aprobada || '-'}</div>
                      </div>
                      <div className="rounded bg-green-50 p-2">
                        <div className="text-gray-500">Usado</div>
                        <div className="font-semibold">{detalle.cantidad_utilizada || '-'}</div>
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-gray-700">
                      <div className="font-semibold text-gray-900">Mis depósitos</div>
                      {depositosAdmin.length ? (
                        depositosAdmin.map((deposito) => (
                          <div key={deposito.id} className="flex justify-between border-b py-1 last:border-b-0">
                            <span>{deposito.nombre}</span>
                            <span className="font-semibold">{stockEnDeposito(detalle.componente_id, deposito.id)}</span>
                          </div>
                        ))
                      ) : (
                        <span className="text-gray-500">Sin depósitos asignados</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {solicitudAprobar && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Aprobar y transferir #{solicitudAprobar.id}</h3>
                <p className="text-sm text-gray-600">
                  Pedido #{solicitudAprobar.pedido_id} · {solicitudAprobar.pedido?.colegio || 'Sin colegio'}
                </p>
              </div>
              <button
                type="button"
                onClick={cerrarModalAprobacion}
                className="px-3 py-2 border rounded bg-gray-100 text-gray-700"
              >
                Cerrar
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="rounded border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
                {depositosAdmin.length ? (
                  <>
                    Se intentará descontar desde tus depósitos asignados:{' '}
                    <span className="font-semibold">
                      {depositosAdmin.map((deposito) => deposito.nombre).join(', ')}
                    </span>
                    . Si un depósito no alcanza, el sistema puede repartir la transferencia entre varios.
                  </>
                ) : (
                  'No tenés depósitos asignados. Usá aprobación manual o asigná depósitos al administrador.'
                )}
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <button
                  type="button"
                  onClick={() =>
                    setAprobacionForm((prev) => ({ ...prev, modo_descuento: 'automatico', deposito_id: '' }))
                  }
                  disabled={!depositosAdmin.length}
                  className={`text-left border rounded-lg p-3 transition disabled:opacity-50 ${
                    aprobacionForm.modo_descuento === 'automatico'
                      ? 'border-green-600 bg-green-50 ring-2 ring-green-100'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold text-gray-900">Automático desde mis depósitos</div>
                  <div className="mt-1 text-sm text-gray-500">Transfiere al depósito del técnico desde tus depósitos autorizados.</div>
                </button>

                <button
                  type="button"
                  onClick={() => setAprobacionForm((prev) => ({ ...prev, modo_descuento: 'manual' }))}
                  className={`text-left border rounded-lg p-3 transition ${
                    aprobacionForm.modo_descuento === 'manual'
                      ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-100'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold text-gray-900">Elegir depósito manualmente</div>
                  <div className="mt-1 text-sm text-gray-500">Transfiere toda la solicitud desde un depósito puntual.</div>
                </button>
              </div>

              {aprobacionForm.modo_descuento === 'manual' && (
                <select
                  value={aprobacionForm.deposito_id}
                  onChange={(e) => setAprobacionForm((prev) => ({ ...prev, deposito_id: e.target.value }))}
                  className="w-full border px-3 py-2 rounded"
                >
                  <option value="">Seleccionar depósito</option>
                  {depositos.map((deposito) => (
                    <option key={deposito.id} value={deposito.id}>
                      {deposito.nombre}{deposito.alias ? ` · ${deposito.alias}` : ''}
                    </option>
                  ))}
                </select>
              )}

              <div className="hidden md:block overflow-x-auto border rounded-lg">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-blue-100 text-left">
                      <th className="px-3 py-2">Repuesto</th>
                      <th className="px-3 py-2">Solicitado</th>
                      <th className="px-3 py-2">Disponible para transferir</th>
                      <th className="px-3 py-2">Detalle por depósito</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(solicitudAprobar.detalles || []).map((detalle) => {
                      const disponibleAuto = totalStockEnDepositos(detalle, depositosAdminIds);
                      const alcanza = disponibleAuto >= Number(detalle.cantidad_solicitada || 0);
                      return (
                        <tr key={detalle.id} className="border-t">
                          <td className="px-3 py-2">
                            <div className="font-semibold">{detalle.nombre}</div>
                            <div className="text-xs text-gray-500">{detalle.codigo_unico}</div>
                          </td>
                          <td className="px-3 py-2">{detalle.cantidad_solicitada}</td>
                          <td className="px-3 py-2">
                            <span className={alcanza ? 'text-green-700 font-semibold' : 'text-red-700 font-semibold'}>
                              {disponibleAuto}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-700">
                            {depositosAdmin.length ? (
                              depositosAdmin.map((deposito) => (
                                <div key={deposito.id}>
                                  {deposito.nombre}: {stockEnDeposito(detalle.componente_id, deposito.id)}
                                </div>
                              ))
                            ) : (
                              <span className="text-gray-500">Sin depósitos asignados</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3">
                {(solicitudAprobar.detalles || []).map((detalle) => {
                  const disponibleAuto = totalStockEnDepositos(detalle, depositosAdminIds);
                  const alcanza = disponibleAuto >= Number(detalle.cantidad_solicitada || 0);
                  return (
                    <div key={detalle.id} className="rounded-lg border border-gray-200 p-3">
                      <div className="font-semibold text-gray-900">{detalle.nombre}</div>
                      <div className="text-xs text-gray-500">{detalle.codigo_unico}</div>
                      <div className="mt-3 flex items-center justify-between rounded bg-gray-50 p-2 text-sm">
                        <span>Solicitado</span>
                        <span className="font-semibold">{detalle.cantidad_solicitada}</span>
                      </div>
                      <div className="mt-2 flex items-center justify-between rounded p-2 text-sm bg-blue-50">
                        <span>Disponible para transferir</span>
                        <span className={alcanza ? 'text-green-700 font-semibold' : 'text-red-700 font-semibold'}>
                          {disponibleAuto}
                        </span>
                      </div>
                      <div className="mt-3 text-sm text-gray-700">
                        <div className="font-semibold text-gray-900">Por depósito</div>
                        {depositosAdmin.length ? (
                          depositosAdmin.map((deposito) => (
                            <div key={deposito.id} className="flex justify-between border-b py-1 last:border-b-0">
                              <span>{deposito.nombre}</span>
                              <span className="font-semibold">{stockEnDeposito(detalle.componente_id, deposito.id)}</span>
                            </div>
                          ))
                        ) : (
                          <span className="text-gray-500">Sin depósitos asignados</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <textarea
                value={aprobacionForm.observacion_admin}
                onChange={(e) => setAprobacionForm((prev) => ({ ...prev, observacion_admin: e.target.value }))}
                className="w-full border px-3 py-2 rounded"
                rows={3}
                placeholder="Observación para el técnico (opcional)"
              />
            </div>

            <div className="p-5 border-t flex flex-col md:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={cerrarModalAprobacion}
                className="px-4 py-2 border rounded bg-white text-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={aprobarSolicitud}
                disabled={procesandoId === solicitudAprobar.id}
                className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
              >
                {procesandoId === solicitudAprobar.id ? 'Aprobando...' : 'Aprobar y transferir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
